import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Eye, EyeOff, ArrowLeft, CheckCircle2, UserPlus } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoHead, SEO_PRESETS } from "@/components/SeoHead";
import api, { formatApiError } from "@/lib/api";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [registered, setRegistered] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/register", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
      });
      setRegistered(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-background overflow-x-hidden">
      <SeoHead {...SEO_PRESETS.register} />
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="glow-orb glow-orb-teal w-[600px] h-[600px] -top-40 -right-40" />
        <div className="glow-orb glow-orb-navy w-[500px] h-[500px] -bottom-32 -left-32" />
      </div>

      <header className="relative z-10">
        <div className="container flex h-16 sm:h-20 items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild variant="ghost" data-testid="register-back">
              <Link to="/"><ArrowLeft className="h-4 w-4" /> Home</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 px-4 py-8 sm:py-12 flex items-center justify-center">
        <div className="w-full max-w-md">
          <Card className="glass-card relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
            <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-primary/30 blur-3xl pointer-events-none" />
            <CardContent className="p-7 sm:p-10 relative">
            {registered ? (
              <div className="text-center" data-testid="register-success">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-teal/15 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-7 w-7 text-teal" />
                </div>
                <h1 className="text-xl font-heading font-bold">Pendaftaran Successfully</h1>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  Your account has been registered and will be approved by the Maahad Bursa admin.
                  You will be able to sign in once your access has been approved.
                </p>
                <div className="mt-6 space-y-2">
                  <Button asChild className="w-full" data-testid="register-to-login">
                    <Link to="/login">Back ke Sign In</Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/">Home</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 border border-primary/20 mb-3">
                    <UserPlus className="h-5 w-5 text-primary" />
                  </div>
                  <h1 className="text-2xl font-heading font-bold tracking-tight">
                    Register Account New
                  </h1>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    Register for portal access. Your account will be approved by admin.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input id="name" value={form.name} onChange={(e) => setField("name", e.target.value)} required data-testid="register-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" autoComplete="email" value={form.email} onChange={(e) => setField("email", e.target.value)} required data-testid="register-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input id="phone" type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="01X-XXXXXXX" data-testid="register-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input id="password" type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => setField("password", e.target.value)} required minLength={6} data-testid="register-password" />
                      <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                        {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm">Confirm Password</Label>
                    <Input id="confirm" type={showPwd ? "text" : "password"} value={form.confirm} onChange={(e) => setField("confirm", e.target.value)} required data-testid="register-confirm" />
                  </div>

                  {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}

                  <Button type="submit" className="w-full" size="lg" disabled={submitting} data-testid="register-submit">
                    {submitting ? "Registering..." : "Register Account"}
                  </Button>

                  <p className="text-center text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <Link to="/login" className="text-primary hover:underline font-medium">Sign In</Link>
                  </p>
                </form>
              </>
            )}
          </CardContent>
        </Card>
        </div>
      </main>
    </div>
  );
}
